import { useEffect, useRef, useState } from 'react'
import { InputLabel, TextField, InputAdornment, IconButton, Box } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import UseTranslation from '../../hooks/UseTranslation'
import { ReactComponent as TeamSnap } from '../../images/TeamSnap.svg'

const OfoqPasswordField = ({
  name,
  defaultValue,
  placeholder,
  required,
  refState,
  setValidPassword,
  hideInputValidator,
  customStyle,
  passwordMatch,
  ...props
}) => {
  const splitName = name?.split('_').join(' ')
  const textFieldRef = useRef()
  const { t } = UseTranslation()
  const [showPassword, setShowPassword] = useState(false)
  const [inputTouched, setInputTouched] = useState(false)

  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const handleMouseDownPassword = () => setShowPassword(!showPassword)

  const containSymbols = /^(?=.*[@$#])/
  const notContainSymbols = /^(?=.*[!@#$%^&*()_+])/

  // booleans for password validations
  const [containsULAndN, setContainsULAndN] = useState(false)
  const [containsSymbols, setContainsSymbols] = useState(false)
  const [containsSymbolsFirstOrLast, setContainsSymbolsFirstOrLast] = useState(false)
  const [contains6CTo12C, setContains6CTo12C] = useState(false)

  const [notValidField, setNotValidField] = useState(true)

  const validatePassword = () => {
    const textValue = textFieldRef?.current?.value
    setInputTouched(textValue.length > 0)
    // ◉ Upper ,lowercase letters and number
    if (textValue?.toLowerCase() !== textValue && textValue?.toUpperCase() !== textValue) {
      setContainsULAndN(true)
    } else {
      setContainsULAndN(false)
    }
    // ◉ Contains one of the symbols (@,#,$)
    if (containSymbols.test(textValue)) {
      setContainsSymbols(true)
    } else {
      setContainsSymbols(false)
    }

    // ◉ From 6-12 characters
    if (textValue?.length >= 6 && textValue?.length <= 12) {
      setContains6CTo12C(true)
    } else {
      setContains6CTo12C(false)
    }

    // ◉ not contain any symbols at first or last word
    if (
      notContainSymbols.test(textValue.charAt(0)) ||
      notContainSymbols.test(textValue.charAt(textValue.length - 1))
    ) {
      setContainsSymbolsFirstOrLast(true)
    } else {
      setContainsSymbolsFirstOrLast(false)
    }

    refState(textFieldRef.current.value)
  }
  const getValidationColor = (condition) => {
    if (!inputTouched) return '#6b778c';
    return condition ? 'primary.main' : '#F44336'
  }
  const getValidationBarColor = (condition) => {
    if (!inputTouched) return '#DFE1E5';
    return condition ? 'primary.main' : '#F44336'
  }

  useEffect(() => {
    validatePassword()
    if (!hideInputValidator) {
      if (containsULAndN && containsSymbols && contains6CTo12C && !containsSymbolsFirstOrLast) {
        setNotValidField(true)
        setValidPassword(true)
      } else {
        setNotValidField(false)
        setValidPassword(false)
      }
    }
  }, [defaultValue, containsULAndN, containsSymbols, contains6CTo12C])

  return (
    <Box sx={customStyle}>
      <InputLabel
        htmlFor={name}
        sx={styles.formInputLabel}
      className='password-label'>
        {splitName}
          {required && <TeamSnap />}
      </InputLabel>
      <Box sx={styles.fieldContainer}>
        <TextField
          type={showPassword ? 'text' : 'password'}
          name={name}
          id={name}
          placeholder={placeholder}
          required={!!required}
          sx={[styles.formField, notValidField && styles.formFieldError]}
          inputRef={textFieldRef}
          onChange={validatePassword}
          InputProps={{
            // <-- This is where the toggle button is added.
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="PasswordVisibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}>
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
            autoComplete: 'new-password',
            form: {
              autoComplete: 'off',
            },
          }}
          {...props}
          className='password-field'
        />

        {!hideInputValidator && (
          <Box sx={styles.formPasswordValidator}>
            {containsSymbolsFirstOrLast ? (
              <Box
                className="password-requirements"
                sx={[
                  styles.item,
                  { color: getValidationColor(!containsSymbolsFirstOrLast) },
                ]}>
                {`◉ ${t?.['Special Characters can’t be added as the first or last characters']}`}
              </Box>
            ) : (
              <>
                <Box sx={styles.formPasswordValidatorChart}>
                  <Box
                    sx={[
                      styles.formPasswordValidatorChartItem,
                      {
                        backgroundColor:
                         getValidationBarColor(
                           containsULAndN || containsSymbols || contains6CTo12C,
                         ),
                      },
                    ]}
                  />

                  <Box
                    sx={[
                      styles.formPasswordValidatorChartItem,
                      {
                        backgroundColor:
                         getValidationBarColor(
                           (containsSymbols && containsULAndN) ||
        (containsSymbols && contains6CTo12C) ||
        (containsULAndN && contains6CTo12C),
                         ),
                      },
                    ]}
                  />

                  <Box
                    sx={[
                      styles.formPasswordValidatorChartItem,
                      {
                        backgroundColor:
                         getValidationBarColor(
                           containsULAndN && containsSymbols && contains6CTo12C,
                         ),
                      },
                    ]}
                  />
                </Box>

                <Box className="password-requirements" sx={[styles.item, { color: getValidationColor(containsULAndN) }]}>
                  {`◉ ${t?.['Upper, lowercase letters and numbers']}`}
                </Box>
                <Box className="password-requirements" sx={[styles.item, { color: getValidationColor(containsSymbols) }]}>
                  {`◉ ${t?.['Contains one of the symbols (@,#,$)']}`}
                </Box>
                <Box className="password-requirements" sx={[styles.item, { color: getValidationColor(contains6CTo12C) }]}>
                  {`◉ ${t?.['Between 6-12 characters']}`}
                </Box>
              </>
            )}
          </Box>
        )}

    { passwordMatch !== undefined &&
          <Box sx={styles.formPasswordValidator}>
          <Box className="password-requirements" sx={[styles.item, { color: getValidationColor(passwordMatch) }]}>
               {`◉ ${t?.['Password Match']}`}
              </Box>
          </Box>
    }

      </Box>
    </Box>
  )
}

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
  },

  formField: {
    display: 'flex',
    width: '100%',
    margin: '10px 0 20px 0',
    transition: 'all 0.5s ease',
    input: {
      padding: { xs: '10px', sm: '15px' },
      fontSize: { xs: '14px', sm: '17px' },
    },
    button: {
      svg: {
        width: { xs: '18px', sm: '24px' },
        height: { xs: '18px', sm: '24px' },
      },
    },
    '& .MuiFormHelperText-root': {
      display: 'none',
      color: 'error.main',
      margin: '0',
      marginTop: '5px',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fafbfc',
      overflow: 'hidden',
      borderRadius: '15px',

      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'primary.main',
      },
    },

    '& .MuiOutlinedInput-notchedOutline': {
      border: 'solid 1px #e2e8ee',
      transition: 'all 0.3s ease-in-out',
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-inputMarginDense': {
      padding: '12px',
    },
    '& .MuiAlert-root': {
      width: '100%',
    },
    '& input::placeholder': {
      textTransform: 'capitalize',
      fontSize: { xs: '12px', sm: '14px' },
    },
    '& input': {
      fontSize: { xs: '12px', sm: '14px' },
      height: { xs: '1.5em', md: 'unset' },
    },
    '& svg': {
      width: {
        xs: '20px',
        sm: '30px',
        md: '40px' },
    },
  },

  formInputLabel: {
    display: 'flex',
    alignItems: 'center',
    transform: 'translate(0,0) scale(1)',
    textTransform: 'capitalize',
    color: 'common.black',
    fontSize: { xs: '11px', md: '16px' },
    width: 'max-content',
    paddingRight: { xs: '14px', sm: '18px' },
    '& svg': {
      mx: '10px',
    },
  },

  formFieldError: {
    '& .MuiFormHelperText-root': {
      display: 'flex',
    },
  },

  fieldContainer: {
    position: 'relative',
  },

  numberFieldBtns: {
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: 0,
    top: '10px',

    '& button:nth-of-type(1)': {
      borderTopRightRadius: '5px',
    },

    '& button:nth-of-type(2)': {
      borderBottomRightRadius: '5px',
    },

    '& button': {
      minWidth: '30px',
      maxHeight: '28px',
      backgroundColor: 'primary.main',
      borderRadius: '15px',

      '& svg': {
        fontSize: '16px',
        color: 'white',
      },

      '&:hover': {
        backgroundColor: 'primary.main',
        '& svg': {
          color: 'white',
        },
      },
    },
  },
  item: {
    fontSize: { xs: 10, sm: 14 },
    fontWeight: 500,
    lineHeight: 1.33,
    letterSpacing: 0,
    marginBottom: '13px',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.3s ease-in-out',
    fontFamily: 'Avenir, Roboto, monospace',
  },
  formPasswordValidator: {
    width: '100%',
    marginBottom: 4,
    '& .active': {
      color: 'primary.main',
    },
  },
  formPasswordValidatorChart: {
    width: '100%',
    display: 'flex',
    marginBottom: 2,
  },
  formPasswordValidatorChartItem: {
    width: '100%',
    height: 4,
    display: 'inline-block',
    borderRadius: '50px',
    transition: 'all 0.3s ease-in-out',
    '&:not(:last-of-type)': {
      marginRight: '10px',
    },
  },
}

export default OfoqPasswordField
